import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';

import {
  useBindPublicationOverlayCatalog,
  usePublicationChannelBindings,
  useSetPublicationPrimaryCatalog,
  useUnbindPublicationOverlayCatalog,
} from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import type { PublicationCatalog, PublicationSalesChannel } from '../../model/publicationTypes';

interface PublicationChannelBindingsDialogProps {
  open: boolean;
  channel: PublicationSalesChannel | null;
  /** Every tenant catalog — filtered internally to the channel's own `catalogTypeCode`. */
  catalogs: PublicationCatalog[];
  canManage: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Manage a channel's persisted PRIMARY catalog and ordered OVERLAY catalogs. Combined into one
 * dialog since both configure the same channel's catalog composition.
 */
export function PublicationChannelBindingsDialog({
  open,
  channel,
  catalogs,
  canManage,
  onClose,
  onResult,
}: PublicationChannelBindingsDialogProps) {
  const { t } = useTranslation('catalog');
  const bindingsQuery = usePublicationChannelBindings(channel?.id ?? '', { enabled: open && channel !== null });
  const setPrimary = useSetPublicationPrimaryCatalog();
  const bindOverlay = useBindPublicationOverlayCatalog();
  const unbindOverlay = useUnbindPublicationOverlayCatalog();

  const [primarySelection, setPrimarySelection] = useState('');
  const [overlaySelection, setOverlaySelection] = useState('');
  const [overlayPriority, setOverlayPriority] = useState('10');

  const eligibleCatalogs = catalogs.filter((c) => c.catalogTypeCode === channel?.catalogTypeCode);
  const bindings = bindingsQuery.data ?? { primaryCatalogId: null, overlays: [] };
  const catalogLabel = (catalogId: string) => {
    const found = eligibleCatalogs.find((c) => c.id === catalogId);
    return found ? `${found.name} (${found.code})` : catalogId;
  };
  const overlayEligible = eligibleCatalogs.filter(
    (c) => c.id !== bindings.primaryCatalogId && !bindings.overlays.some((o) => o.catalogId === c.id),
  );

  const pending = setPrimary.isPending || bindOverlay.isPending || unbindOverlay.isPending;
  const showBindings = !bindingsQuery.isLoading && !bindingsQuery.isError;

  const handleError = (error: NormalizedApiError) => onResult('error', error.message);

  const submitPrimary = () => {
    if (!channel || primarySelection === '') return;
    setPrimary.mutate(
      { channelId: channel.id, body: { catalogId: primarySelection } },
      {
        onSuccess: () => {
          onResult('success', t('publicationChannelsPanel.bindingsDialog.primarySet'));
          setPrimarySelection('');
        },
        onError: handleError,
      },
    );
  };

  const submitOverlay = () => {
    if (!channel || overlaySelection === '') return;
    const priority = Number(overlayPriority);
    if (Number.isNaN(priority)) return;
    bindOverlay.mutate(
      { channelId: channel.id, catalogId: overlaySelection, body: { priority } },
      {
        onSuccess: () => {
          onResult('success', t('publicationChannelsPanel.bindingsDialog.overlayAdded'));
          setOverlaySelection('');
        },
        onError: handleError,
      },
    );
  };

  const removeOverlay = (catalogId: string) => {
    if (!channel) return;
    unbindOverlay.mutate(
      { channelId: channel.id, catalogId },
      {
        onSuccess: () => onResult('success', t('publicationChannelsPanel.bindingsDialog.overlayRemoved')),
        onError: handleError,
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {channel
          ? t('publicationChannelsPanel.bindingsDialog.title', { name: channel.name })
          : t('publicationChannelsPanel.bindingsDialog.titleFallback')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          {bindingsQuery.isLoading ? <LoadingState label={t('publicationChannelsPanel.bindingsDialog.loading')} /> : null}
          {bindingsQuery.isError ? (
            <ErrorState message={bindingsQuery.error?.message} onRetry={() => void bindingsQuery.refetch()} />
          ) : null}

          {showBindings ? (
            <>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('publicationChannelsPanel.bindingsDialog.primaryHeading')}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {bindings.primaryCatalogId ? (
                    <Chip size="small" color="primary" label={catalogLabel(bindings.primaryCatalogId)} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('publicationChannelsPanel.bindingsDialog.primaryEmpty')}
                    </Typography>
                  )}
                </Stack>
                {canManage ? (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                    <TextField
                      select
                      size="small"
                      label={t('publicationChannelsPanel.bindingsDialog.selectCatalogLabel')}
                      value={primarySelection}
                      onChange={(e) => setPrimarySelection(e.target.value)}
                      fullWidth
                      disabled={pending || eligibleCatalogs.length === 0}
                    >
                      {eligibleCatalogs.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name} ({c.code})
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      onClick={submitPrimary}
                      disabled={pending || primarySelection === ''}
                      startIcon={setPrimary.isPending ? <CircularProgress size={16} /> : undefined}
                    >
                      {t('publicationChannelsPanel.bindingsDialog.setPrimaryButton')}
                    </Button>
                  </Stack>
                ) : null}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('publicationChannelsPanel.bindingsDialog.overlaysHeading')}
                </Typography>
                {bindings.overlays.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('publicationChannelsPanel.bindingsDialog.overlaysEmpty')}
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {bindings.overlays.map((o) => (
                      <ListItem
                        key={o.catalogId}
                        disablePadding
                        secondaryAction={
                          canManage ? (
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={t('publicationChannelsPanel.bindingsDialog.removeOverlayAria', {
                                catalog: catalogLabel(o.catalogId),
                              })}
                              onClick={() => removeOverlay(o.catalogId)}
                              disabled={pending}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          ) : undefined
                        }
                      >
                        <ListItemText
                          primary={catalogLabel(o.catalogId)}
                          secondary={t('publicationChannelsPanel.bindingsDialog.priorityLabel', {
                            priority: o.priority,
                          })}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {canManage ? (
                  <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                    <TextField
                      select
                      size="small"
                      label={t('publicationChannelsPanel.bindingsDialog.selectCatalogLabel')}
                      value={overlaySelection}
                      onChange={(e) => setOverlaySelection(e.target.value)}
                      sx={{ flex: 2 }}
                      disabled={pending || overlayEligible.length === 0}
                    >
                      {overlayEligible.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name} ({c.code})
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="number"
                      label={t('publicationChannelsPanel.bindingsDialog.priorityInputLabel')}
                      value={overlayPriority}
                      onChange={(e) => setOverlayPriority(e.target.value)}
                      sx={{ flex: 1 }}
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      disabled={pending}
                    />
                    <Button
                      variant="outlined"
                      onClick={submitOverlay}
                      disabled={pending || overlaySelection === ''}
                      startIcon={bindOverlay.isPending ? <CircularProgress size={16} /> : undefined}
                    >
                      {t('publicationChannelsPanel.bindingsDialog.addOverlayButton')}
                    </Button>
                  </Stack>
                ) : null}
              </Box>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:action.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
